import { useLocale } from "@/lib/i18n/intl";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import { NotificationBell } from "./NotificationBell";

type Props = {
  /** Matches MobileScreenHeader's `theme` so the trigger sits on either chrome. */
  theme?: "light" | "dark";
  /** Override the trigger style (e.g. the bordered-circle bell in the store navbar). */
  triggerClassName?: string;
};

/**
 * Self-wiring NotificationBell for the mobile screen headers. Desktop renders
 * the bell in the AppShell top bar, which doesn't exist on mobile — this
 * wrapper resolves the locale/store context itself so the shared
 * `MobileScreenHeader` can surface the same bell (same panel, same unread
 * badge) on every primary screen without each page re-plumbing the props. The
 * notifications feed itself is read from the shared `NotificationsProvider`, so
 * mounting several of these does not open extra Firestore listeners.
 */
export function MobileHeaderNotificationBell({ theme = "light", triggerClassName }: Props) {
  const storeSlug = useSellerStoreSlug();
  const locale = useLocale();
  if (!storeSlug) return null;
  return (
    <NotificationBell
      locale={locale}
      storeSlug={storeSlug}
      triggerClassName={
        triggerClassName ??
        (theme === "dark"
          ? "relative !h-11 !w-11 !rounded-full !bg-[#1d2430] !text-[#cbd2dd] hover:brightness-110"
          : "relative !h-11 !w-11 !rounded-full !bg-[color:var(--surface-alt)] !text-[color:var(--text-secondary)]")
      }
    />
  );
}
